import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Signal,
  signal,
  untracked,
  viewChildren
} from '@angular/core';
import { ContractActionService } from '../contract-action.service';
import {
  KeyValueLabel,
  LoaderComponent,
  RecapCard,
  RecapCardAttributeModification,
  RecapCardComponent,
  RecapCardDates,
  RecapCardSignalComponent,
  SideBarItem,
  SideNavbarComponent,
  TRANSLATE_SERVICE_TOKEN,
  TranslateInterface
} from '@tribeauto/ngx-mobilite-material';
import { TranslateService } from '@ngx-translate/core';
import { ComponentVisibilityService } from '../../component-visibility.service';
import { ComponentTrackerDirective } from '../../../helpers/component-tracker.directive';
import { ApplicationStore } from '../../../../core/services/application-store.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { groupBy, map, mergeMap, Observable, pairwise, switchMap, tap } from 'rxjs';
import { formatLocaleDate } from '../../../helpers/date.helper';
import { form } from '@angular/forms/signals';

@Component({
  selector: 'pvmo-resume',
  imports: [SideNavbarComponent, LoaderComponent, ComponentTrackerDirective, RecapCardSignalComponent],
  providers: [
    {
      provide: TRANSLATE_SERVICE_TOKEN,
      useFactory: (translateService: TranslateService): TranslateInterface => ({
        get: (key: string): string => translateService.instant(key)
      }),
      deps: [TranslateService]
    }
  ],
  templateUrl: './resume.component.html',
  styleUrl: './resume.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResumeComponent {
  private readonly contractActionService = inject(ContractActionService);
  private readonly applicationStore = inject(ApplicationStore);
  private readonly visibilityComponentService = inject(ComponentVisibilityService);
  private readonly recapCards = viewChildren('recapCards');

  resumeGroup = signal<RecapCardDates>({});
  resumeForm = form(this.resumeGroup);

  contractRecapList: Signal<RecapCard[]> = computed(() => this.contractActionService.lastFetchedRecapCardList());

  editablePositions = computed(() => {
    const positions = new Map<string, { i: number; j: number }>();
    this.contractRecapList().forEach((recap, i) => {
      recap.holderAttributeList?.forEach((attribute, j) => {
        if (attribute.isModifiable && !positions.has('holder' + j)) {
          positions.set('holder' + j, { i, j });
        }
      });
    });
    return positions;
  });

  contractRecapListError = computed(() => {
    return this.contractActionService.lastFetchedRecapCardListError();
  });

  sideNavBarItemList: Signal<SideBarItem[]> = computed(() => {
    return this.mapSideNavBarList(this.contractRecapList());
  });
  currentComponent = this.visibilityComponentService.topVisibleComponent;
  defaultSelectedSideBar = signal(this.sideNavBarItemList()[0]?.keyValueLabel);

  constructor() {
    effect(() => {
      const list = this.contractRecapList();
      const positions = this.editablePositions();
      untracked(() => {
        this.resumeGroup.set(
          Object.fromEntries(
            [...positions].map(([key, { i, j }]) => [
              key,
              list[i].holderAttributeList[j].content ? new Date(list[i].holderAttributeList[j].content) : null
            ])
          )
        );
      });
    });
    effect(() => {
      const component = this.currentComponent();
      if (component) {
        this.defaultSelectedSideBar.set(
          this.sideNavBarItemList().find(item => item.keyValueLabel.key === component.id)?.keyValueLabel ||
            this.sideNavBarItemList()[0]?.keyValueLabel
        );
      }
    });

    toObservable(this.resumeGroup)
      .pipe(
        tap(value => console.log(value)),
        pairwise(),
        mergeMap(([previous, current]) =>
          Object.keys(current)
            .filter(key => key in previous && current[key] && !this.isSameDay(current[key], previous[key]))
            .map(key => ({ key, date: current[key] }))
        ),
        groupBy(({ key }) => key),
        mergeMap(byKey$ => byKey$.pipe(switchMap(({ key, date }) => this.saveDeliveryDate(key, date ?? new Date())))),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  mapSideNavBarList(recapCardList: RecapCard[]): SideBarItem[] {
    const sideNavBarList: SideBarItem[] = [];
    for (const recapCard of recapCardList) {
      const index = recapCardList.indexOf(recapCard);
      if (this.atLeastOneAttributeHasContent(recapCard)) {
        const item: SideBarItem = {
          icon: recapCard.iconName,
          keyValueLabel: { key: `recap-${index}`, value: recapCard.title.replace('application.recap-card', '') }
        };
        sideNavBarList.push(item);
      }
    }
    return sideNavBarList;
  }

  navigateToRecapCard(item: KeyValueLabel): void {
    const element = document.querySelectorAll(`[data-component-id="${item.key}"]`)[0];
    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest'
    });
  }

  atLeastOneAttributeHasContent(recapCard: RecapCard): boolean {
    return recapCard.holderAttributeList.some(attribute => attribute.content);
  }

  updateDeliveryDate(deliveryDate: Date): Observable<Date> {
    const numAuto = this.applicationStore.instant('actionsData')()!.cckNumber!;
    return this.contractActionService.updateVehicleDeliveryDate(numAuto, deliveryDate).pipe(map(() => deliveryDate));
  }

  private saveDeliveryDate(key: string, date: Date): Observable<Date> {
    const deliveryDate = new Date(date);
    deliveryDate.setHours(12, 0, 0, 0);
    const { i, j } = this.editablePositions().get(key)!;

    return this.updateDeliveryDate(deliveryDate).pipe(
      tap(value => {
        const resumeInfo = (this.recapCards()[i] as RecapCardComponent).recapInfo();
        const holderAttribute = resumeInfo.holderAttributeList[j] as RecapCardAttributeModification;
        holderAttribute.content = formatLocaleDate(value);
        holderAttribute.isBeingModified = false;
      })
    );
  }

  private readonly isSameDay = (a: Date | null, b: Date | null): boolean =>
    !!a && !!b && a.toDateString() === b.toDateString();
}
